import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  from,
  InMemoryCache,
  type NormalizedCacheObject,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { type ErrorResponse, onError } from '@apollo/client/link/error';
import { relayStylePagination } from '@apollo/client/utilities';
import { RouterProvider } from '@tanstack/react-router';
import React from 'react';
import { createRoot, type Root } from 'react-dom/client';

import { ToastContainer } from 'react-toastify';
import { API_PATH } from 'src/constants';
import { removeTokenAndExpire } from 'src/helpers/authHelper';
import reportWebVitals from 'src/reportWebVitals';
import { router } from 'src/router';
import 'react-toastify/dist/ReactToastify.css';
import 'src/styles/reactToastTheme.css';

console.log(`react version: ${React.version}`);

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element not found');
}

const root: Root = createRoot(container);

interface AuthHeaders {
  headers: Record<string, string>;
}

const authLink = setContext((_: unknown, { headers }: { headers?: Record<string, string> }): AuthHeaders => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      ...headers,
      authorization: token ? `JWT ${token}` : '',
    },
  };
});

const errorLink = onError(({ graphQLErrors, networkError }: ErrorResponse): void => {
  if (graphQLErrors) {
    const errors = graphQLErrors.map((e) => e.message);
    if (errors.includes('Signature has expired')) {
      removeTokenAndExpire();
      window.location.replace('/');
    }
  }
  if (networkError) {
    // TODO: handle any network errors
  }
});

export const client: ApolloClient<NormalizedCacheObject> = new ApolloClient({
  link: from([authLink, errorLink, createHttpLink({ uri: API_PATH, credentials: 'include' })]),
  uri: API_PATH,
  cache: new InMemoryCache({
    addTypename: true,
    typePolicies: {
      CollectionNodeType: {
        fields: {
          items: relayStylePagination(),
        },
      },
    },
  }),
});

root.render(
  <ApolloProvider client={client}>
    <ToastContainer
      position="top-right"
      autoClose={5000}
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme="light"
    />
    <RouterProvider router={router} />
  </ApolloProvider>,
);

reportWebVitals();
